/**
 * Compress an image file/blob before upload.
 * Resizes to maxDimension and compresses to target quality.
 * Returns a Blob ready for upload.
 * Includes a timeout — falls back to the original file if compression takes too long.
 */
export function compressImage(file, { maxDimension = 1600, quality = 0.8, timeoutMs = 5000 } = {}) {
  return new Promise((resolve) => {
    const timer = setTimeout(() => {
      // Timeout: return original file
      resolve(file);
    }, timeoutMs);

    try {
      const img = new Image();
      const url = URL.createObjectURL(file);

      img.onload = () => {
        clearTimeout(timer);
        URL.revokeObjectURL(url);

        try {
          let { width, height } = img;

          if (width > maxDimension || height > maxDimension) {
            const ratio = Math.min(maxDimension / width, maxDimension / height);
            width = Math.round(width * ratio);
            height = Math.round(height * ratio);
          }

          const canvas = document.createElement('canvas');
          canvas.width = width;
          canvas.height = height;

          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);

          canvas.toBlob(
            (blob) => {
              resolve(blob || file);
            },
            'image/jpeg',
            quality
          );
        } catch {
          resolve(file);
        }
      };

      img.onerror = () => {
        clearTimeout(timer);
        URL.revokeObjectURL(url);
        resolve(file);
      };

      img.src = url;
    } catch {
      clearTimeout(timer);
      resolve(file);
    }
  });
}
